import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { useEffect } from 'react';
import { authenticateUserFromStorage } from '../../features/sessions/sessionSlice';
import * as Types from '../../#interfaces/interfaces';
import { useNavigate, useParams } from 'react-router-dom';
import { ContentPlayer } from '../audioPlayer/WaveSurfer/contentPlayer/ContentPlayer';
import { getUserContents } from '../../features/sessions/sessionAudioContent/sliceUserContents';
import * as REQUEST_REQUIREMENTS from '../../api/requestRequirements';
import { toast } from 'react-toastify';
import { postProjectContent } from '../../features/sessions/projectsManagement/sessionManageProject';
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

export default function MyContentsFooter() {
    const currentUser = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const userValidated = useSelector((state: RootState) => state.session.loggedIn);
    const contents = useSelector((state: RootState) => state.sessionUserContents.userContents);
    const newcontents = useSelector((state: RootState) => state.sessionUserContents.newMyContents);
    const errors = useSelector((state: RootState) => state.sessionManageProjectContents.errors);
    const dispatch = useDispatch<AppDispatch>();
    const { userNicknameRoute, projectIdRoute } = useParams();
    const navigate = useNavigate();
    const loading = useSelector((state: RootState) => state.sessionUserContents.loading);
    const [contentsPage, setContentsPage] = useState(1);
    const loadingMoreContents = useSelector((state: RootState) => state.sessionUserContents.loadingMoreContents);
    const MemorizedContentPlayer = useMemo(() => React.memo(ContentPlayer), []);

    useEffect(() => {
        async function authenticateUser() {
            if (!userValidated) {
                await dispatch(authenticateUserFromStorage());
            }
        }

        async function handleMyContents(authHeaders: any, currentUser: any) {
            await dispatch(getUserContents({
                authHeaders: authHeaders,
                routeParams: {
                    userNickname: currentUser?.nickname,
                    contentsPage: contentsPage,
                },
            }));
        }

        authenticateUser();
        handleMyContents(authHeaders, currentUser);
    }, [contentsPage]);

    useEffect(() => {
        const handleScroll = () => {
            const container = document.getElementById('my-contents-footer-container');
            if (!container) return;
            const { scrollLeft, clientWidth, scrollWidth } = container;
            if (scrollLeft + clientWidth >= scrollWidth && !loadingMoreContents) {
                setContentsPage(contentsPage + 1);
            }
        };

        setTimeout(() => {
            const container = document.getElementById('my-contents-footer-container');
            if (container) {
                container.addEventListener('scroll', handleScroll);
                return () => container.removeEventListener('scroll', handleScroll);
            }
        }, 100);
    }, [loadingMoreContents]);

    async function handleUploadProjectContent(item: Types.AudioContent) {
        if (projectIdRoute) {
            const postProjectContentParams = {
                authHeaders: authHeaders,
                projectContentsRouteParams: {
                    userNicknameRoute: userNicknameRoute,
                    projectIdRoute: projectIdRoute,
                },
                postBody: { id: item.id },
            };

            await dispatch(postProjectContent(postProjectContentParams));

            if (errors.length === 0) {
                const routeParams = {
                    userNickname: userNicknameRoute,
                    projectId: projectIdRoute,
                };
                const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: routeParams });
                navigate(PRIVATE_ROUTES.PROJECT_CONTENTS);
            } else {
                errors.map((err) => { toast.error(err); });
            }
        }
    }

    const allItems = [...newcontents, ...contents];

    return (
        <Box
            id='my-contents-footer-container'
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                bgcolor: BG_SECTION,
                borderTop: BORDER_SUBTLE,
                zIndex: 100,
                userSelect: 'none',
                overflowX: 'auto',
                overflowY: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    width: 'max-content',
                    minWidth: '100%',
                }}
            >
                {allItems.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 1,
                            width: 300,
                            flexShrink: 0,
                            bgcolor: BG_CARD,
                            border: BORDER_SUBTLE,
                            borderRadius: '10px',
                            px: 1,
                            py: 0.75,
                        }}
                    >
                        {/* Chip instrumento */}
                        <Box sx={{ width: 72, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                            <Chip
                                label={item.instrument?.name ?? 'other'}
                                size='small'
                                sx={{
                                    bgcolor: BRAND_RED,
                                    color: '#fff',
                                    fontWeight: 600,
                                    maxWidth: 72,
                                    fontSize: '0.65rem',
                                }}
                            />
                        </Box>

                        {/* Título + waveform */}
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography
                                variant='caption'
                                color={TEXT_PRIMARY}
                                noWrap
                                sx={{ fontWeight: 500, lineHeight: 1.2 }}
                            >
                                {item.title}
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <MemorizedContentPlayer fileUrl={`${item.fileUrl}`} />
                            </Box>
                        </Box>

                        {/* Botão de anexar */}
                        <Tooltip title='Attach to project' placement='top'>
                            <IconButton
                                size='small'
                                onClick={() => handleUploadProjectContent(item)}
                                sx={{
                                    flexShrink: 0,
                                    color: TEXT_SECONDARY,
                                    '&:hover': {
                                        color: BRAND_RED,
                                        bgcolor: 'rgba(192,57,43,0.10)',
                                    },
                                }}
                            >
                                <FileUploadIcon fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ))}

                {loadingMoreContents && (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                        <CircularProgress size={28} sx={{ color: BRAND_RED }} />
                    </Box>
                )}
            </Box>
        </Box>
    );
}
